import React, { useEffect, useState } from 'react';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Link,
  Theme,
} from '@mui/material';
import { Alert } from '@mui/material';
import moment from 'moment-timezone';
import makeStyles from '@mui/styles/makeStyles';
import { IDownload, IFolder, ITick, ITick2 } from '~/_base/components/helixIcons';
import ApiCall from '~/_base/functions/apicalls';
import api from '~/_base/constants/api';
import useSWR from 'swr';
import StepOne from './stepOne';
import StepTwo from './stepTwo';
import StepThree from './stepThree';
import StepFour from './stepFour';
import ConfirmHuman from './confirmHuman';
import getGeoInformation from '../../../_base/functions/getGeoInformation';

require('./animations.css');

const isDevelopment = process.env.NODE_ENV === 'development';

export interface Customer {
  recaptcha_code_v2?: string;
  recaptcha_code_v3?: string;
  email: string;
  organisation_name: string;
  given_name: string;
  surname: string;
  telephone_number: string;
  subdomain: string;
  ip_address: string;
  country_code: string;
  currency_code: string;
  country: string;
  time_zone: string;
}

export default function App() {
  const expanded = useMediaQuery('(min-width:600px)');
  const classes = useStyles({ expanded });
  const [formState, setFormState] = useState<Partial<Customer>>({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [step, setStep] = useState(1);
  const [failed, setFailed] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    function getCurrencyCode(countryCode: string, continentCode: string) {
      if (countryCode === 'AU') {
        return 'AUD';
      } else if (countryCode === 'GB') {
        return 'GBP';
      } else if (countryCode === 'NZ') {
        return 'NZD';
      } else if (countryCode === 'ZA') {
        return 'ZAR';
      } else if (continentCode === 'EU' && countryCode !== 'GB') {
        return 'EUR';
      } else {
        return 'USD';
      }
    }
    const resGeo = getGeoInformation('geocode');
    const resIp = getGeoInformation('client_ip');
    if (resGeo && resIp && !isDevelopment) {
      setFormState(prev => ({
        ...prev,
        country_code: resGeo.countryCode,
        country: resGeo.countryName,
        ip_address: resIp.ipAddress,
        currency_code: getCurrencyCode(
          resGeo.countryCode,
          resGeo.continentCode
        ),
      }));
    }
  }, []);

  const { data: badDomains } = useSWR(
    api.badEmailDomains.address(),
    api.badEmailDomains.request
  );

  function handleNext(data: Partial<Customer>) {
    setFormState({ ...formState, ...data });
    setStep(step + 1);
  }

  function handlePrevious() {
    setStep(step - 1);
  }

  function pollForCompletion(address: string) {
    let attempt = 0;
    const pollInterval = 3;
    const timeOut = 600;
    poll();

    async function poll() {
      if (attempt >= timeOut) {
        setFailed(true);
      } else {
        const json: { redirect_to?: string } = await ApiCall(address, 'GET');
        if (json?.redirect_to) {
          const address = json.redirect_to;
          setSuccess(true);
          setTimeout(() => window.location.replace(address), 2500);
        } else {
          attempt = attempt + pollInterval;
          setTimeout(() => {
            poll();
          }, pollInterval * 1000);
        }
      }
    }
  }

  async function handleCreate(customer: Partial<Customer>) {
    setShowSpinner(true);
    const timezone = moment.tz.guess();
    const payload = JSON.stringify({
      ...formState,
      time_zone: timezone ?? 'UTC',
      ...customer,
    });

    try {
      const res = await api.submitTrialSetup.request(payload);
      if (res.ok) {
        const json: { poll: string } = await res.json();
        pollForCompletion(json.poll);
      } else if (res.status === 401) {
        setShowSpinner(false);
        setStep(5);
      } else {
        setFailed(true);
      }
    } catch (err) {
      console.log(err);
      setFailed(true);
    }
  }

  function renderStep() {
    switch (step) {
      case 1:
        return (
          badDomains && (
            <StepOne
              onNext={handleNext}
              formState={formState}
              badDomains={badDomains}
            />
          )
        );
      case 2:
        return (
          <StepTwo
            onNext={handleNext}
            onPrevious={handlePrevious}
            formState={formState}
          />
        );
      case 3:
        return (
          <StepThree
            onNext={handleNext}
            onPrevious={handlePrevious}
            formState={formState}
          />
        );
      case 4:
        return (
          <StepFour
            onCreate={token => handleCreate({ recaptcha_code_v3: token })}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <ConfirmHuman onSubmit={handleCreate} onPrevious={handlePrevious} />
        );
      default:
        return (
          badDomains && (
            <StepOne
              onNext={handleNext}
              formState={formState}
              badDomains={badDomains}
            />
          )
        );
    }
  }

  return (
    <React.Fragment>
      <div className={classes.headerContainer}>
        <div className={classes.header}>
          <div className={classes.headerText}>
            <Typography className={classes.headerPrimaryText} variant="h1">
              Start free trial
            </Typography>
            <Typography variant="h3" className={classes.headerSecondaryText}>
              Welcome to Damstra Form's free 30 day trial
            </Typography>
          </div>
        </div>
      </div>
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.formContainer}>
            {failed ? (
              <Alert severity="error">
                <div style={{ marginBottom: 8 }}>
                  There was an error processing your information and we were
                  unable to complete it successfully
                </div>
                <Link href="https://damstra.zendesk.com/hc/en-us/requests/new?ticket_form_id=900001882346">
                  Please contact support to continue.
                </Link>
              </Alert>
            ) : success ? (
              <div className={classes.successMessage}>
                <ITick2 className={classes.successIcon} />
                Your site is now ready. Please wait while we redirect you...
              </div>
            ) : showSpinner ? (
              <React.Fragment>
                <div className="spinner">
                  <Typography variant="h1" className={classes.sendingText}>
                    Sending your information...
                  </Typography>
                  <Typography variant="h1" className={classes.processingText}>
                    We're processing your information and creating your trial
                    server...
                  </Typography>
                  <div className="bounce1" />
                  <div className="bounce2" />
                  <div className="bounce3" />
                </div>
              </React.Fragment>
            ) : (
              renderStep()
            )}
          </div>
          <div className={classes.featureBox}>
            <Typography variant="h4" className={classes.featureBoxText}>
              Included in your free 30 day trial
            </Typography>
            <List>
              <ListItem disableGutters>
                <ListItemIcon>
                  <IFolder className={classes.featureBoxText} />
                </ListItemIcon>
                <ListItemText disableTypography>
                  <Typography className={classes.featureBoxText}>
                    Unlimited use of example templates
                  </Typography>
                </ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon>
                  <span className={classes.featureBoxText} style={{ fontSize: '24px', width: '24px', textAlign: 'center' }}>∞</span>
                </ListItemIcon>
                <ListItemText disableTypography>
                  <Typography className={classes.featureBoxText}>
                    Unlimited form creation, data collection and users
                  </Typography>
                </ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon>
                  <IDownload className={classes.featureBoxText} />
                </ListItemIcon>
                <ListItemText disableTypography>
                  <Typography className={classes.featureBoxText}>
                    Access to the Damstra Forms iOS, Android and Windows Apps
                  </Typography>
                </ListItemText>
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon>
                  <ITick className={classes.featureBoxText} />
                </ListItemIcon>
                <ListItemText disableTypography>
                  <Typography className={classes.featureBoxText}>
                    No credit card required
                  </Typography>
                </ListItemText>
              </ListItem>
            </List>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

const useStyles = makeStyles<Theme, { expanded: boolean }>(theme => ({
  headerContainer: {
    paddingTop: 45,
    display: 'flex',
    height: 350,
    maxHeight: ({ expanded }) => (expanded ? '20rem' : '10rem'),
    minWidth: '100vw',
    backgroundSize: 'cover',
    backgroundImage:
      'url(https://www.damstratechnology.com/assets/img/bg-light-desktop.png)',
  },
  header: {
    width: '100%',
    maxWidth: 950,
    margin: 'auto',
  },
  headerText: {
    padding: ({ expanded }) => (expanded ? theme.spacing(6) : theme.spacing(3)),
  },
  headerSecondaryText: {
    marginTop: theme.spacing(1),
  },
  headerPrimaryText: {
    fontSize: ({ expanded }) => (expanded ? 65 : 40),
    fontWeight: 500,
  },
  content: {
    width: '100%',
    maxWidth: 950,
    margin: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: ({ expanded }) => (expanded ? 'row' : 'column'),
  },
  contentContainer: { backgroundColor: '#fff' },
  formContainer: {
    width: ({ expanded }) => (expanded ? '60%' : '100%'),
    padding: ({ expanded }) => (expanded ? theme.spacing(6) : theme.spacing(3)),
    boxSizing: 'border-box',
  },
  featureBox: {
    boxSizing: 'border-box',
    backgroundColor: '#001e35',
    padding: ({ expanded }) => (expanded ? theme.spacing(7) : theme.spacing(4)),
    width: ({ expanded }) => (expanded ? '50%' : '100%'),
  },
  featureBoxText: {
    color: '#fff',
  },
  sendingText: {
    color: '#6f6f6f',
  },
  processingText: {
    margin: theme.spacing(3, 0),
  },
  successMessage: {
    padding: 0,
    background: 'transparent',
    color: '#191919',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: '1.1rem',
  },
  successIcon: {
    color: '#b1e41f',
    fontSize: '4rem',
    marginBottom: '1rem',
  },
}));
